/** Keyboard-only focus rings for scripted focus moves. */
(() => {
  if (window.YiseFocusVisibility) return;

  const nativeFocus = HTMLElement.prototype.focus;
  const nativeBlur = HTMLElement.prototype.blur;
  if (typeof nativeFocus !== 'function' || typeof nativeBlur !== 'function') return;

  const NAVIGATION_KEYS = new Set(['Tab']);
  const CONTROL_NAVIGATION_KEYS = new Set([
    'Enter',
    ' ',
    'Spacebar',
    'ArrowUp',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'Home',
    'End',
    'PageUp',
    'PageDown',
  ]);

  const TEXT_ENTRY_SELECTOR = [
    'input:not([type="button"],[type="checkbox"],[type="color"],[type="file"],[type="hidden"],[type="image"],[type="radio"],[type="range"],[type="reset"],[type="submit"])',
    'textarea',
    '[contenteditable]:not([contenteditable="false"])',
  ].join(',');

  function isTextEntry(node) {
    return node instanceof Element && node.matches(TEXT_ENTRY_SELECTOR);
  }

  function eventSource(event) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    return path[0] || event.target;
  }

  let hadKeyboardEvent = true;

  const root = document.documentElement;
  const publishModality = () => {
    const modality = hadKeyboardEvent ? 'keyboard' : 'pointer';
    if (root.dataset.focusModality !== modality) root.dataset.focusModality = modality;
  };
  publishModality();

  window.addEventListener(
    'keydown',
    (event) => {
      if (event.metaKey || event.altKey || event.ctrlKey) return;
      if (typeof event.key !== 'string') return;

      const isNavigation =
        NAVIGATION_KEYS.has(event.key) || (CONTROL_NAVIGATION_KEYS.has(event.key) && !isTextEntry(eventSource(event)));

      if (!isNavigation) return;
      hadKeyboardEvent = true;
      publishModality();
      restoreLegacyFocusStyle(deepActiveElement(document));
    },
    { capture: true, passive: true }
  );

  const onPointerInput = (event) => {
    if (!event.isTrusted) return;
    hadKeyboardEvent = false;
    publishModality();
  };

  ['pointerdown', 'mousedown', 'touchstart'].forEach((type) => {
    window.addEventListener(type, onPointerInput, { capture: true, passive: true });
  });

  let supportsFocusVisible = false;
  try {
    const probe = {};
    Object.defineProperty(probe, 'focusVisible', {
      get() {
        supportsFocusVisible = true;
        return true;
      },
    });
    nativeFocus.call(document.createElement('button'), probe);
  } catch {
    supportsFocusVisible = false;
  }

  window.YiseFocusVisibility = { patched: supportsFocusVisible };
  root.dataset.focusVisiblePatched = String(supportsFocusVisible);

  const legacyFocusStyles = new WeakMap();

  function deepActiveElement(ownerDocument) {
    let activeElement = ownerDocument.activeElement;
    while (activeElement?.shadowRoot?.activeElement) {
      activeElement = activeElement.shadowRoot.activeElement;
    }
    return activeElement;
  }

  function restoreLegacyFocusStyle(element) {
    const styles = legacyFocusStyles.get(element);
    if (!styles) return;

    legacyFocusStyles.delete(element);
    ['outline', 'box-shadow'].forEach((property) => {
      const saved = styles[property];
      if (saved.value) element.style.setProperty(property, saved.value, saved.priority);
      else element.style.removeProperty(property);
    });
  }

  function suppressLegacyFocusStyle(element) {
    if (legacyFocusStyles.has(element)) return;

    const styles = {};
    ['outline', 'box-shadow'].forEach((property) => {
      styles[property] = {
        value: element.style.getPropertyValue(property),
        priority: element.style.getPropertyPriority(property),
      };
      element.style.setProperty(property, 'none', 'important');
    });
    legacyFocusStyles.set(element, styles);
    element.addEventListener('blur', () => restoreLegacyFocusStyle(element), { once: true });
  }

  function quietCurrentFocus(element) {
    if (!(element instanceof HTMLElement) || isTextEntry(element)) return;
    const focusRoot = element.getRootNode();
    if (focusRoot.activeElement !== element || !element.matches(':focus-visible')) return;

    if (element instanceof HTMLSelectElement) {
      suppressLegacyFocusStyle(element);
      return;
    }

    if (!supportsFocusVisible) {
      suppressLegacyFocusStyle(element);
      return;
    }

    nativeBlur.call(element);
    nativeFocus.call(element, { preventScroll: true, focusVisible: false });
    if (focusRoot.activeElement !== element) nativeFocus.call(element, { preventScroll: true });
  }

  HTMLElement.prototype.focus = function focus(options) {
    const keepEngineDefault =
      hadKeyboardEvent ||
      isTextEntry(this) ||
      (options !== null && typeof options === 'object' && 'focusVisible' in options);

    if (keepEngineDefault) {
      restoreLegacyFocusStyle(this);
      nativeFocus.call(this, options);
      return;
    }

    if (deepActiveElement(this.ownerDocument) === this) {
      quietCurrentFocus(this);
      return;
    }

    if (supportsFocusVisible) {
      nativeFocus.call(this, { ...options, focusVisible: false });
      if (deepActiveElement(this.ownerDocument) === this && this.matches(':focus-visible')) {
        suppressLegacyFocusStyle(this);
      }
    } else {
      nativeFocus.call(this, options);
      quietCurrentFocus(this);
    }
  };

  const nativeShowModal = typeof HTMLDialogElement === 'function' ? HTMLDialogElement.prototype.showModal : null;

  if (typeof nativeShowModal === 'function') {
    HTMLDialogElement.prototype.showModal = function showModal() {
      nativeShowModal.call(this);
      if (!hadKeyboardEvent) quietCurrentFocus(deepActiveElement(this.ownerDocument));
      this.addEventListener(
        'close',
        () => {
          if (hadKeyboardEvent) return;
          requestAnimationFrame(() => quietCurrentFocus(deepActiveElement(this.ownerDocument)));
        },
        { once: true }
      );
    };
  }

  document.addEventListener(
    'close',
    (event) => {
      if (!(event.target instanceof HTMLDialogElement)) return;
      if (!hadKeyboardEvent) quietCurrentFocus(deepActiveElement(event.target.ownerDocument));
    },
    { capture: true }
  );

  window.addEventListener(
    'click',
    () => {
      if (hadKeyboardEvent) return;
      requestAnimationFrame(() => quietCurrentFocus(deepActiveElement(document)));
    },
    { capture: true, passive: true }
  );
})();
